import { useRequestExampleValue } from '@/business-components/request-parameter/hooks/useRequestExampleValue';
import usePersistFn from '@/hooks/usePersistFn';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import {
  getSampleCodeSelectedLanguageVariant,
  setSampleCodeSelectedLanguageVariant,
} from '@/shared/constant/local-storage/sample-code-selected-language-variant/SampleCodeSelectedLanguageVariant';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { generateRequestSampleCode } from '@/shared/util/generateRequestSampleCode';
import React from 'react';

export function useSelectSampleCodeValue(
  value: HttpApiServerType,
  extraData: ResourceHttpApiData['extraData'],
) {
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);

  const [codeValue, setCode] = React.useState(extraData.defaultRequestSampleCode?.code);
  const [prompt, setPrompt] = React.useState<string | undefined>(undefined);
  const [codeBlockLanguage, setCodeBlockLanguage] = React.useState(
    extraData.defaultRequestSampleCode?.codeBlockLanguage,
  );

  const selectedSampleCodeLanguage = useOperationConfigModelStore(
    (x) => x.selectedSampleCodeLanguage,
  );
  const selectedSampleCodeVariant = useOperationConfigModelStore(
    (x) => x.selectedSampleCodeVariant,
  );
  const setSelectedSampleCodeLanguageVariant = useOperationConfigModelStore(
    (x) => x.setSelectedSampleCodeLanguageVariant,
  );
  const { selectedRequestExample, requestExamples, setSelectedRequestKey } = useRequestExampleValue(
    value.requestBody.examples,
  );

  const generateRequestSampleCodeFn = usePersistFn(
    async (options: { language: string; variant: string } | undefined) => {
      if (
        (options &&
          (options.language !== extraData.defaultRequestSampleCode?.language ||
            options.variant !== extraData.defaultRequestSampleCode?.variant)) ||
        selectedRequestExample?.key !== requestExamples[0]?.key
      ) {
        try {
          const defaultRequestSampleCode = await generateRequestSampleCode(
            value,
            extraData.requestCollection,
            selectedEnvironment?.variables ?? [],
            options,
            selectedRequestExample?.value,
          );
          setCode(defaultRequestSampleCode?.code);
          setPrompt(defaultRequestSampleCode?.prompt);
          setCodeBlockLanguage(defaultRequestSampleCode?.codeBlockLanguage);
          setSelectedSampleCodeLanguageVariant(
            defaultRequestSampleCode
              ? {
                  language: defaultRequestSampleCode.language,
                  variant: defaultRequestSampleCode.variant,
                }
              : undefined,
          );
          return;
        } catch (error) {
          console.error(error);
        }
      }

      setCode(extraData.defaultRequestSampleCode?.code);
      setPrompt(extraData.defaultRequestSampleCode?.prompt);
      setCodeBlockLanguage(extraData.defaultRequestSampleCode?.codeBlockLanguage);
      setSelectedSampleCodeLanguageVariant(options);
    },
  );

  React.useEffect(() => {
    const localLanguageVariant = getSampleCodeSelectedLanguageVariant();
    generateRequestSampleCodeFn(localLanguageVariant);
  }, [generateRequestSampleCodeFn, value, extraData, selectedRequestExample]);

  const setSelectedSampleCodeLanguage = usePersistFn(async (language: string | undefined) => {
    if (language) {
      const currentLanguage = value.codeSampleLanguages.find((x) => x.key === language);
      const currentVariant = currentLanguage?.variants[0]?.key;

      if (currentLanguage && currentVariant) {
        await generateRequestSampleCodeFn({
          language,
          variant: currentVariant,
        });
        setSampleCodeSelectedLanguageVariant(language, currentVariant);
        return;
      }
    }

    generateRequestSampleCodeFn(undefined);
    setSampleCodeSelectedLanguageVariant(undefined, undefined);
  });

  const setSelectedSampleCodeVariant = usePersistFn(async (variant: string) => {
    if (!selectedSampleCodeLanguage) {
      return;
    }
    await generateRequestSampleCodeFn({
      language: selectedSampleCodeLanguage,
      variant,
    });
    setSampleCodeSelectedLanguageVariant(selectedSampleCodeLanguage, variant);
  });

  const selectedLanguageVariants = React.useMemo(() => {
    if (selectedSampleCodeLanguage) {
      return (
        value.codeSampleLanguages.find((x) => x.key === selectedSampleCodeLanguage)?.variants ?? []
      );
    } else {
      return value.codeSampleLanguages[0]?.variants ?? [];
    }
  }, [selectedSampleCodeLanguage, value.codeSampleLanguages]);

  return {
    prompt,
    codeValue,
    codeBlockLanguage,
    selectedSampleCodeLanguage,
    selectedSampleCodeVariant,
    setSelectedSampleCodeLanguage,
    setSelectedSampleCodeVariant,
    selectedLanguageVariants,
    selectedRequestExample,
    requestExamples,
    setSelectedRequestKey,
  };
}
